<script setup lang="ts">
import { ChevronLeft, ChevronRight, Trash2 } from "@lucide/vue";
import { useI18n } from "vue-i18n";

import type { CompanyPhoto } from "@/entities/company";
import { Badge, Button } from "@/shared/ui";

const props = defineProps<{
  photo: CompanyPhoto;
  index: number;
  total: number;
  isFirst: boolean;
  isLast: boolean;
  reorderInFlight: boolean;
}>();

const emit = defineEmits<{
  moveLeft: [];
  moveRight: [];
  delete: [];
}>();

const { t } = useI18n();
</script>

<template>
  <div
    role="listitem"
    class="relative aspect-square overflow-hidden rounded-md border border-border"
  >
    <!-- Photo -->
    <img
      :src="props.photo.url"
      loading="lazy"
      :alt="t('salon.gallery.a11y.photoAlt', { n: props.index + 1 })"
      class="size-full object-cover"
    />

    <!-- Cover badge -->
    <Badge
      v-if="props.index === 0"
      class="absolute left-1 top-1 text-xs"
    >
      {{ t('salon.gallery.a11y.cover') }}
    </Badge>

    <!-- Overlay action bar — always visible (mobile-first) -->
    <div
      class="absolute bottom-0 left-0 right-0 flex items-center justify-end gap-0.5 bg-background/80 px-0.5 py-0.5"
    >
      <Button
        variant="ghost"
        size="icon-sm"
        :disabled="props.isFirst || props.reorderInFlight"
        :aria-label="t('salon.gallery.a11y.moveLeft', { n: props.index + 1 })"
        @click="emit('moveLeft')"
      >
        <ChevronLeft aria-hidden="true" />
      </Button>

      <Button
        variant="ghost"
        size="icon-sm"
        :disabled="props.isLast || props.reorderInFlight"
        :aria-label="t('salon.gallery.a11y.moveRight', { n: props.index + 1 })"
        @click="emit('moveRight')"
      >
        <ChevronRight aria-hidden="true" />
      </Button>

      <Button
        variant="ghost"
        size="icon-sm"
        :aria-label="t('salon.gallery.a11y.delete', { n: props.index + 1 })"
        @click="emit('delete')"
      >
        <Trash2 aria-hidden="true" />
      </Button>
    </div>
  </div>
</template>
